import type { ChangeEvent } from "react";
import { useState } from "react";
import axios from "axios";
import type { AirlineScore } from "services/scores";

import { useTranslations } from "contexts/LocalizationContext";
import { Flex, Heading, Text, Select } from "@chakra-ui/react";
import Card from "./Card";
import type { BestAirlinesProps } from "./BestAirlines.d";
import ScoreTag from "../ScoreTag";
import { AIRLINES_COUNTRIES, DEFAULT_SELECTED_COUNTRY } from "./consts";

const BestAirlinesByCountry = ({ airlinesScoresByCountry }: Pick<BestAirlinesProps, "airlinesScoresByCountry">) => {
  const [bestAirlines, setBestAirlines] = useState(airlinesScoresByCountry);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SELECTED_COUNTRY);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const translations = useTranslations();

  const onCountryChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
    setCountriesLoading(true);

    try {
      const { data } = await axios.get<AirlineScore[]>(
        `/api/scores/get-airlines-by-country/?country=${e.target.value}`
      );
      setBestAirlines(data);
    } catch (error) {
      setBestAirlines(airlinesScoresByCountry);
      setSelectedCountry(DEFAULT_SELECTED_COUNTRY);
      throw Error(error.message);
    } finally {
      setCountriesLoading(false);
    }
  };

  return (
    <Card>
      <Heading variant="h3" as="h3" mb={2}>
        {translations("best_airlines_by_country")}
      </Heading>
      <Text fontSize="md" mb={6}>
        {translations("out_of_the_airlines")}
      </Text>
      <Select
        h="48px"
        maxWidth={{ base: "100%", md: "55%" }}
        mb={4}
        value={selectedCountry}
        onChange={onCountryChange}
        disabled={countriesLoading}
      >
        {AIRLINES_COUNTRIES.map(({ code, name }) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </Select>
      {bestAirlines.map((airline, index) => {
        const { airline: name, score } = airline;
        return (
          <Flex
            key={name}
            justifyContent="space-between"
            alignItems="center"
            fontSize="sm"
            borderBottom="1px"
            borderColor="greyscale.300"
            py={4}
            position="relative"
            _last={{ borderBottom: "none" }}
            _after={{
              content: `""`,
              position: "absolute",
              bottom: -1,
              height: 2,
              width: 6,
              bgColor: "white",
            }}
          >
            <Flex>
              <Text fontSize="inherit" mr={4}>
                {index + 1}.
              </Text>
              <Text fontSize="inherit">{name}</Text>
            </Flex>
            <ScoreTag index={index} score={score} />
          </Flex>
        );
      })}
    </Card>
  );
};

export default BestAirlinesByCountry;
